import colorRef from './reference/color.json';
import spacingRef from './reference/spacing.json';
import typographyRef from './reference/typography.json';
import radiusRef from './reference/radius.json';
import colorLight from './system/color.light.json';
import colorDark from './system/color.dark.json';
import { logger } from '../utils/logger';

// Helper to extract hex values from token structure
function getHexValue(token: any): string {
  if (typeof token === 'string') return token;
  if (token.$value?.hex) return token.$value.hex;
  if (token.$value?.components) {
    const [r, g, b] = token.$value.components;
    const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  return '#000000';
}

function getDimensionValue(token: any): string {
  if (typeof token === 'string') return token;
  if (token.$value?.value && token.$value?.unit) {
    return `${token.$value.value}${token.$value.unit}`;
  }
  return '0px';
}

// Extract reference colors
export const referenceColors = {
  green: Object.fromEntries(
    Object.entries(colorRef.color.green).map(([key, token]) => [key, getHexValue(token)])
  ),
  amber: Object.fromEntries(
    Object.entries((colorRef.color as any).amber || {}).map(([key, token]) => [key, getHexValue(token)])
  ),
  brown: Object.fromEntries(
    Object.entries(colorRef.color.brown).map(([key, token]) => [key, getHexValue(token)])
  ),
  cream: Object.fromEntries(
    Object.entries(colorRef.color.cream).map(([key, token]) => [key, getHexValue(token)])
  ),
  gray: Object.fromEntries(
    Object.entries(colorRef.color.gray).map(([key, token]) => [key, getHexValue(token)])
  ),
  red: Object.fromEntries(
    Object.entries(colorRef.color.red).map(([key, token]) => [key, getHexValue(token)])
  ),
  white: getHexValue(colorRef.color.white),
  black: getHexValue(colorRef.color.black),
};

// Extract spacing
export const spacing = Object.fromEntries(
  Object.entries(spacingRef.spacing).map(([key, token]) => [key, getDimensionValue(token)])
);

// Extract typography
export const typography = {
  family: {
    body: typographyRef.font.family.body.$value,
    heading: typographyRef.font.family.heading.$value,
  },
  size: Object.fromEntries(
    Object.entries(typographyRef.font.size).map(([key, token]) => [key, getDimensionValue(token)])
  ),
  weight: Object.fromEntries(
    Object.entries(typographyRef.font.weight).map(([key, token]) => [key, token.$value])
  ),
  lineHeight: Object.fromEntries(
    Object.entries(typographyRef.font.lineHeight).map(([key, token]) => [key, token.$value])
  ),
};

// Extract radius
export const radius = Object.fromEntries(
  Object.entries(radiusRef.radius).map(([key, token]) => [key, getDimensionValue(token)])
);

// Resolve system colors (simplified - in production use a proper resolver)
function resolveColorToken(token: any, refColors: any): string {
  // Extract value if it's a token object
  let value = token;
  if (token && typeof token === 'object' && token.$value) {
    value = token.$value;
  }
  
  // Now handle the actual value
  if (typeof value === 'string') {
    // Handle references like {color.green.500}
    const match = value.match(/\{color\.(\w+)\.(\w+)\}/);
    if (match) {
      const [, family, shade] = match;
      const resolved = refColors[family]?.[shade];
      if (resolved) return resolved;
      logger.warn(`Color not found: ${value}`, 'tokens', { family, shade });
      return '#FF0000'; // Red fallback to see the issue
    }
    // Handle {color.white} or {color.black}
    const simpleMatch = value.match(/\{color\.(\w+)\}/);
    if (simpleMatch) {
      const [, colorName] = simpleMatch;
      const resolved = refColors[colorName];
      if (resolved) return resolved;
      logger.warn(`Color not found: ${value}`, 'tokens', { colorName });
      return '#FF0000'; // Red fallback
    }
    return value;
  }
  
  // If it's an object with hex value, use that
  if (token && typeof token === 'object') {
    return getHexValue(token);
  }
  
  return '#000000'; // Final fallback
}

export const systemColors = {
  light: Object.fromEntries(
    Object.entries(colorLight.color).map(([key, token]) => [
      key,
      resolveColorToken(token, referenceColors),
    ])
  ),
  dark: Object.fromEntries(
    Object.entries(colorDark.color).map(([key, token]) => [
      key,
      resolveColorToken(token, referenceColors),
    ])
  ),
};

